/*
 * @Description: 地图点位 —「管理点位」弹窗（对齐设计图）
 *   工具栏（搜索 + 添加点位 + 批量导入）+ 点位表格（图标/名称+创建时间/地址/经纬度/操作）。
 *   阶段一：列表 + 搜索 + 删除已落地；添加点位 / 批量导入为阶段二、三，先占位。
 */
import React, { useState } from 'react'
import { Modal, Input, Button, Table, Popconfirm, message } from 'antd'
import {
  EnvironmentOutlined,
  LeftOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { ColumnProps } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useAppDispatch } from '../../../../app/hooks'
import { deletePoi } from '../../../../actions/locationCockpit'
import { isCockpitMockEnabled, POI_MAX_COUNT } from '../../constants'
import { usePoiList } from '../../hooks/usePoiList'
import { CockpitEnvelope } from '../../types/cockpit.types'
import { PoiItem } from '../../types/poi.types'
import PoiEditor from './PoiEditor'
import PoiImporter from './PoiImporter'
import './index.scss'

interface PoiManagerProps {
  /** 是否显示 */
  visible: boolean
  /** 当前团队长 id */
  groupId: string
  /** 关闭 */
  onClose: () => void
  /** 选中/新增点位 → 定位地图到该点位（由调用方关闭弹窗并居中放大） */
  onFocusPoi: (lng: number, lat: number) => void
}

const PoiManager: React.FC<PoiManagerProps> = ({
  visible,
  groupId,
  onClose,
  onFocusPoi
}) => {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [keyword, setKeyword] = useState('')
  const { pois, total, loading, refresh } = usePoiList(
    groupId,
    keyword,
    visible
  )

  // 已达 50 上限禁用「添加点位」（关键词为空时 total 即团队全量）
  const reachLimit = total >= POI_MAX_COUNT

  // 列表 / 编辑 / 批量导入 视图
  const [mode, setMode] = useState<'list' | 'edit' | 'import'>('list')
  const [editing, setEditing] = useState<PoiItem | null>(null)

  const handleDelete = (id: number) => {
    if (isMock) {
      message.success('已删除（mock 模式不持久）')
      return
    }
    dispatch(deletePoi({ groupId, id }))
      .then((res: CockpitEnvelope<unknown>) => {
        if (res && res.code === 200) {
          message.success('删除成功')
          refresh()
        } else {
          message.error((res && res.toast_msg) || '删除失败')
        }
      })
      .catch(() => message.error('删除失败'))
  }

  const handleAdd = () => {
    setEditing(null)
    setMode('edit')
  }
  const handleEdit = (row: PoiItem) => {
    setEditing(row)
    setMode('edit')
  }
  const backToList = () => setMode('list')
  // 保存成功（新增/编辑）→ 定位地图到该点位（由父级关闭弹窗并居中放大）
  const handleSaved = (coord: { lng: number; lat: number }) => {
    setMode('list')
    onFocusPoi(coord.lng, coord.lat)
  }
  const handleClose = () => {
    setMode('list')
    onClose()
  }
  const handleImport = () => setMode('import')
  const handleImported = () => {
    setMode('list')
    refresh()
  }

  const columns: ColumnProps<PoiItem>[] = [
    {
      title: '图标',
      dataIndex: 'iconUrl',
      width: 64,
      render: (iconUrl: string) =>
        iconUrl ? (
          <img className="poi-manager__icon" src={iconUrl} alt="" />
        ) : (
          <span className="poi-manager__icon poi-manager__icon--fallback">
            <EnvironmentOutlined />
          </span>
        )
    },
    {
      title: '点位名称',
      dataIndex: 'name',
      render: (name: string, row: PoiItem) => (
        <div>
          <div className="poi-manager__name">{name}</div>
          <div className="poi-manager__time">
            {dayjs(row.createdAt).format('YYYY-MM-DD')} 添加
          </div>
        </div>
      )
    },
    { title: '地址', dataIndex: 'address' },
    {
      title: '经纬度',
      width: 120,
      render: (_text: unknown, row: PoiItem) => (
        <div className="poi-manager__lnglat">
          <div>{row.lat.toFixed(4)}</div>
          <div>{row.lng.toFixed(4)}</div>
        </div>
      )
    },
    {
      title: '操作',
      width: 150,
      render: (_text: unknown, row: PoiItem) => (
        <span className="poi-manager__ops">
          <span
            className="poi-manager__link"
            onClick={() => onFocusPoi(row.lng, row.lat)}
          >
            定位
          </span>
          <span className="poi-manager__link" onClick={() => handleEdit(row)}>
            编辑
          </span>
          <Popconfirm
            title="确定删除该点位？"
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleDelete(row.id)}
          >
            <span className="poi-manager__del">删除</span>
          </Popconfirm>
        </span>
      )
    }
  ]

  const title =
    mode === 'list' ? (
      '管理点位'
    ) : (
      <span className="poi-manager__title-back" onClick={backToList}>
        <LeftOutlined />{' '}
        {mode === 'import' ? '批量录入' : editing ? '编辑点位' : '添加点位'}
      </span>
    )

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      centered
      // 高度随屏幕自适应、内部滚动（列表表头吸顶，编辑/导入子视图超高时滚动）
      styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {mode === 'edit' ? (
        <PoiEditor
          groupId={groupId}
          editing={editing}
          onCancel={backToList}
          onSaved={handleSaved}
        />
      ) : mode === 'import' ? (
        <PoiImporter
          groupId={groupId}
          onCancel={backToList}
          onDone={handleImported}
        />
      ) : (
        <>
          <div className="poi-manager__toolbar">
            <Input.Search
              className="poi-manager__search"
              placeholder="搜索点位名称或地址..."
              allowClear
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(v) => setKeyword(v)}
            />
            <div className="poi-manager__actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={reachLimit}
                onClick={handleAdd}
              >
                添加点位
              </Button>
              <Button icon={<UploadOutlined />} onClick={handleImport}>
                批量导入
              </Button>
            </div>
          </div>

          {reachLimit && (
            <div className="poi-manager__limit">
              已达 {POI_MAX_COUNT} 个点位上限，删除后可再添加
            </div>
          )}

          <Table
            className="poi-manager__table"
            rowKey="id"
            columns={columns}
            dataSource={pois}
            loading={loading}
            pagination={false}
            scroll={{ y: 'calc(100vh - 420px)' }}
            locale={{ emptyText: '暂无点位，点击「添加点位」创建' }}
          />
        </>
      )}
    </Modal>
  )
}

export default PoiManager
